import { createApp, defineAsyncComponent, h, type App as VueApp, type Component } from 'vue'
import { createPinia } from 'pinia'
import '@hotwired/turbo-rails'
import { Application } from '@hotwired/stimulus'
import '../styles/application.css'

// Stimulus setup
import ReservationFormController from '../controllers/reservation_form_controller'
import PostalCodeController from '../controllers/postal_code_controller'
import QrPollController from '../controllers/qr_poll_controller'
import ToggleFieldController from '../controllers/toggle_field_controller'
import CouponCodeGeneratorController from '../controllers/coupon_code_generator_controller'
import RowLinkController from '../controllers/row_link_controller'
import RecaptchaController from '../controllers/recaptcha_controller'

const stimulusApp = Application.start()
stimulusApp.register('reservation-form', ReservationFormController)
stimulusApp.register('postal-code', PostalCodeController)
stimulusApp.register('qr-poll', QrPollController)
stimulusApp.register('toggle-field', ToggleFieldController)
stimulusApp.register('coupon-code-generator', CouponCodeGeneratorController)
stimulusApp.register('row-link', RowLinkController)
stimulusApp.register('recaptcha', RecaptchaController)

// Import components
import AppHeader from '../components/layout/AppHeader.vue'
// FullCalendarを含むカレンダーコンポーネントは動的importでコード分割
// （FullCalendar関連で約155KiBあり、カレンダーページでのみ必要）
const CalendarLoadError = { render: () => h('div', { class: 'text-center py-8 text-red-600' }, 'カレンダーの読み込みに失敗しました。ページを再読み込みしてください。') }
const PublicCalendar = defineAsyncComponent({
  loader: () => import('../components/calendar/PublicCalendar.vue'),
  errorComponent: CalendarLoadError,
  timeout: 10000,
})
const ReservationCalendar = defineAsyncComponent({
  loader: () => import('../components/calendar/ReservationCalendar.vue'),
  errorComponent: CalendarLoadError,
  timeout: 10000,
})
import PublicSite from '../components/public/PublicSite.vue'
import PricingBreakdown from '../components/reservation/PricingBreakdown.vue'
import StripePaymentForm from '../components/reservation/StripePaymentForm.vue'
import PaymentAwaitingConfirmation from '../components/reservation/PaymentAwaitingConfirmation.vue'
import ReservationChangeSubmit from '../components/reservation/ReservationChangeSubmit.vue'

// Vue Router
import { createAppRouter } from '../router'

// Vue component registry
const components: Record<string, Component> = {
  AppHeader,
  PublicCalendar,
  ReservationCalendar,
  PublicSite,
  PricingBreakdown,
  StripePaymentForm,
  PaymentAwaitingConfirmation,
  ReservationChangeSubmit,
}

// Track mounted Vue apps for cleanup
// WeakMapだとbody置換時にGCタイミングで不整合が起きる可能性があるため、
// Mapに変更してunmount時に確実に削除する
const mountedApps = new Map<HTMLElement, VueApp>()

// DOMから切り離された古いアプリをクリーンアップ
// (Turboのbody置換後、古いDOM要素はisConnected=falseになる)
// NOTE: ここではel.innerHTMLクリア不要 — 要素自体がDOMツリーから切り離されておりTurboキャッシュに影響しない
function cleanupStaleApps(): void {
  for (const [el, app] of mountedApps) {
    if (!el.isConnected) {
      try {
        app.unmount()
      } catch {
        // unmount時のエラーは無視
      }
      mountedApps.delete(el)
    }
  }
}

// Mount Vue components on elements with data-vue-component attribute
function mountComponents(): void {
  // まず古いアプリをクリーンアップ（body置換後の孤立アプリを除去）
  cleanupStaleApps()

  document.querySelectorAll<HTMLElement>('[data-vue-component]').forEach((el) => {
    const componentName = el.dataset.vueComponent
    if (!componentName) return

    // Skip if already mounted (同じDOM要素に対する二重マウントを防止)
    if (mountedApps.has(el)) {
      return
    }

    const component = components[componentName]
    if (!component) {
      console.warn(`Vue component "${componentName}" not found`)
      return
    }

    // Parse props from data attributes
    const props: Record<string, unknown> = {}
    Object.keys(el.dataset).forEach((key) => {
      if (key !== 'vueComponent' && key.startsWith('prop')) {
        const propName = key.replace('prop', '').replace(/^[A-Z]/, (c) => c.toLowerCase())
        try {
          props[propName] = JSON.parse(el.dataset[key] || '{}')
        } catch {
          props[propName] = el.dataset[key]
        }
      }
    })

    const app = createApp(component, props)
    app.use(createPinia())

    // PublicSiteにはVue Routerを注入
    if (componentName === 'PublicSite') {
      const initialPath = (props.currentPath as string) || window.location.pathname
      const router = createAppRouter(initialPath)
      app.use(router)

      // Router ready待ちしてからマウント（一瞬TOPページが表示される問題を防止）
      router.isReady().then(() => {
        // 非同期なので、マウント前に再度チェック
        if (!mountedApps.has(el) && el.isConnected) {
          app.mount(el)
          mountedApps.set(el, app)
        }
      }).catch((err) => {
        console.error('Router initialization failed:', err)
      })
      return // 同期的なmount/set処理をスキップ
    }

    app.mount(el)
    mountedApps.set(el, app)
  })
}

// Unmount Vue apps (Turboのページ置換・キャッシュ前に呼ばれる)
function unmountComponents(): void {
  for (const [el, app] of mountedApps) {
    try {
      app.unmount()
      // Vue 3のapp.unmount()はDOMノード自体を削除しないため、
      // Turboキャッシュに古いVue描画内容（例: Vue Routerで遷移後のTOPページ）が
      // 残ってプレビュー時にちらつく問題を防ぐ
      el.innerHTML = ''
    } catch {
      // unmount時のエラーは無視（DOMが既に切り離されている場合など）
    }
  }
  mountedApps.clear()
}

// Auto-mount on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mountComponents)
} else {
  mountComponents()
}

// Unmount before Turbo caches the page
// (turbo:before-renderは使わない: Turboプレビュー→本番レンダリングの間で
// Vueコンポーネントが破棄され、Stripe等の非同期初期化が中断されるため)
document.addEventListener('turbo:before-cache', unmountComponents)

// Re-mount on Turbo navigation
document.addEventListener('turbo:load', mountComponents)

// Re-mount on Turbo render (422等のエラーレスポンス時にturbo:loadは発火せず
// turbo:renderのみが発火するため、Vueコンポーネントの再マウントが必要)
document.addEventListener('turbo:render', mountComponents)

// Export for use in other modules
export { mountComponents }
